/**
 * What was typed / scanned into the one search box (T11-07, design/04 §0 "ช่องค้นหาเดียว"):
 *
 * | input | kind | first call |
 * |---|---|---|
 * | `144975412` (cert 8–10 digits), `4521329312345` (JAN 8/13 digits), `Y15-CRD-000123` (item code) | `code` | `GET /v1/admin/variants/lookup?code=` — one hit + Enter opens the product at once |
 * | `#10001`, `S-2026-000123`, `P-2026-000045`, `E-…` | `order` | `GET /v1/admin/orders?q=` |
 * | `CON-00128` | `consignor` | `GET /v1/admin/consignors?q=` |
 * | anything else (≥ 2 characters) | `text` | products + orders + customers + consignors together (debounced 200 ms) |
 *
 * Full-width characters from a Japanese keyboard (`１４４９７５４１２`, `ＣＯＮ－００１２８`) are
 * folded to ASCII first.
 */

export type QueryKind = "empty" | "code" | "order" | "consignor" | "text";

export interface ResolvedQuery {
  kind: QueryKind;
  /** Trimmed, NFKC-folded input (what is sent to the API). */
  term: string;
}

/** Certificate numbers (PSA 8–9 digits, BGS/CGC 10) and JAN / EAN barcodes (8 or 13). */
const DIGITS_CODE = /^\d{8,13}$/;
/**
 * Shop item codes `{PREFIX}-{TYPE}-{NNNNNN}` (catalog `app/domain/sku.py`, prefix chosen by the
 * owner — `Y15` in the reference shop) and hand-typed codes with the same letter-dash-digit shape.
 */
const ITEM_CODE = /^[A-Z0-9]{2,10}-[A-Z]{2,4}-\d{3,}$/i;
/** `#10001` or `S-2026-000123` / `P-…` (POS) / `E-…` (eBay, later) / `W-…` — order numbers. */
const ORDER_NUMBER = /^(#\d{3,}|[A-Z]-\d{4}-\d{3,}|[A-Z]-\d{3,})$/i;
/** Consignor references `CON-00128`. */
const CONSIGNOR_REF = /^CON-?\d{3,}$/i;

export function normalizeInput(raw: string): string {
  return raw
    .normalize("NFKC")
    .replace(/[‐-―−ーｰ]/g, (ch) =>
      // dashes typed on a Japanese keyboard between letters/digits → "-" (ー stays inside words)
      ch === "ー" || ch === "ｰ" ? ch : "-",
    )
    .replace(/\s+/g, " ")
    .trim();
}

export function resolveQuery(raw: string): ResolvedQuery {
  const term = normalizeInput(raw);
  if (!term) {
    return { kind: "empty", term };
  }
  const compact = term.replace(/\s/g, "");
  if (CONSIGNOR_REF.test(compact)) {
    return { kind: "consignor", term: compact.toUpperCase() };
  }
  if (ORDER_NUMBER.test(compact)) {
    return { kind: "order", term: compact.replace(/^#/, "").toUpperCase() };
  }
  if (DIGITS_CODE.test(compact) || ITEM_CODE.test(compact)) {
    return { kind: "code", term: compact.toUpperCase() };
  }
  return { kind: "text", term };
}

/** Text searches start at 2 characters (1 kana/kanji is too broad — design/07 §4.1). */
export const MIN_TEXT_LENGTH = 2;
export const TEXT_DEBOUNCE_MS = 200;
